import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ActionButton,
  Alert,
  Box,
  DataTable,
  FormControlLabel,
  HairlineCard,
  IconButton,
  Icons,
  RowAction,
  RowActions,
  Stack,
  StatusChip,
  Switch,
  Tab,
  Tabs,
  Typography,
  iconSize,
  statusTone,
  useRowActionsColumn,
} from '@munifocus/ui';
import type { InspectionRow } from '@munifocus/contracts-gov';
import { AppShell } from '../../components/AppShell';
import { PageIntro } from '../../components/PageIntro';
import { useAuth } from '../../providers/AuthProvider';
import { useScope } from '../../providers/ScopeProvider';
import { formatDateTime, intlLocale } from '../../lib/relative-time';
import {
  onDay,
  useActiveMunicipality,
  useInspections,
  useModuleSwitches,
  weekStart,
} from '../../lib/permits-api';
import { ScheduleInspectionDialog } from './ScheduleInspectionDialog';
import { ChecklistTemplatesPanel } from './ChecklistTemplatesPanel';
import { frenchFirstOfMonth } from '@munifocus/utils';

const DAY = 86_400_000;

/**
 * The inspection calendar: a week at a time, or the list of what is coming, for
 * the whole municipality or just the signed-in inspector. Schedulers book from
 * here; whoever sets up the checklists edits them from the last tab.
 */
export function Inspections() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { tab } = useParams<{ tab?: string }>();
  const { can } = useScope();
  const { user } = useAuth();
  const lg = useActiveMunicipality();
  const modules = useModuleSwitches();
  const [mine, setMine] = useState(!can('inspection:schedule'));
  const [scheduling, setScheduling] = useState(false);

  const tabs = [
    { key: 'calendar', label: t('gov.inspections.tabs.calendar') },
    { key: 'list', label: t('gov.inspections.tabs.list') },
    ...(can('inspection:configure')
      ? [{ key: 'checklists', label: t('gov.inspections.tabs.checklists') }]
      : []),
  ];
  const active = tabs.find((x) => x.key === tab)?.key ?? 'calendar';
  const inspectorId = mine ? (user?.id ?? '') : '';

  return (
    <AppShell
      title={t('gov.inspections.title')}
      actions={
        lg && modules.inspections && can('inspection:schedule') ? (
          <ActionButton
            label={t('gov.inspections.schedule.title')}
            icon={Icons.Plus}
            onClick={() => setScheduling(true)}
          />
        ) : undefined
      }
    >
      <PageIntro>{t('gov.inspections.intro')}</PageIntro>
      {!lg ? (
        <Alert severity="info">{t('gov.permits.pickMunicipality')}</Alert>
      ) : !modules.inspections ? (
        <Alert severity="info">{t('gov.inspections.notEnabled')}</Alert>
      ) : (
        <Stack spacing={2}>
          <Stack direction="row" sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
            <Tabs
              value={active}
              onChange={(_, v: string) => navigate(`/inspections/view/${v}`)}
              aria-label={t('gov.inspections.title')}
              sx={{ flex: 1 }}
            >
              {tabs.map((x) => (
                <Tab key={x.key} value={x.key} label={x.label} />
              ))}
            </Tabs>
            {active !== 'checklists' && (
              <FormControlLabel
                control={<Switch checked={mine} onChange={(e) => setMine(e.target.checked)} />}
                label={t('gov.inspections.onlyMine')}
              />
            )}
          </Stack>
          {active === 'calendar' && <WeekCalendar lg={lg} inspectorId={inspectorId} />}
          {active === 'list' && <UpcomingList lg={lg} inspectorId={inspectorId} />}
          {active === 'checklists' && <ChecklistTemplatesPanel lg={lg} />}
        </Stack>
      )}
      {lg && scheduling && (
        <ScheduleInspectionDialog
          lg={lg}
          open
          onClose={() => setScheduling(false)}
          onScheduled={(ins) => navigate(`/inspections/${ins.id}`)}
        />
      )}
    </AppShell>
  );
}

function InspectionChip({ row }: { row: InspectionRow }) {
  const { t } = useTranslation();
  return row.status === 'completed' && row.outcome ? (
    <StatusChip
      status={statusTone.inspectionResult[row.outcome]}
      label={t(`gov.inspections.outcomes.${row.outcome}`)}
    />
  ) : (
    <StatusChip
      status={statusTone.inspection[row.status]}
      label={t(`gov.inspections.statuses.${row.status}`)}
    />
  );
}

function WeekCalendar({ lg, inspectorId }: { lg: string; inspectorId: string }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [start, setStart] = useState(() => weekStart(new Date()));
  const days = useMemo(
    () => Array.from({ length: 7 }, (_, i) => new Date(start.getTime() + i * DAY)),
    [start],
  );
  const range = useMemo(
    () => ({
      from: start.toISOString(),
      to: new Date(start.getTime() + 7 * DAY - 1).toISOString(),
    }),
    [start],
  );
  const list = useInspections(lg, range, inspectorId);
  const fr = i18n.language.startsWith('fr');
  const locale = intlLocale(i18n.language);
  const dayLabel = new Intl.DateTimeFormat(locale, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  });
  const timeLabel = new Intl.DateTimeFormat(locale, { hour: '2-digit', minute: '2-digit' });
  const today = new Date();

  return (
    <Stack spacing={1.5}>
      {/* Wraps at phone width: a fixed 220px title plus "Cette semaine" ran
          past a 390px screen in French. */}
      <Stack
        direction="row"
        spacing={1}
        sx={{ alignItems: 'center', flexWrap: 'wrap', rowGap: 0.5 }}
      >
        <IconButton
          aria-label={t('gov.inspections.calendar.previous')}
          onClick={() => setStart(new Date(start.getTime() - 7 * DAY))}
        >
          <Icons.ChevronLeft size={iconSize.md} />
        </IconButton>
        <Typography variant="subtitle1" sx={{ minWidth: { xs: 0, sm: 220 }, textAlign: 'center' }}>
          {t('gov.inspections.calendar.weekOf', {
            date: frenchFirstOfMonth(dayLabel.format(start)),
          })}
        </Typography>
        <IconButton
          aria-label={t('gov.inspections.calendar.next')}
          onClick={() => setStart(new Date(start.getTime() + 7 * DAY))}
        >
          <Icons.ChevronRight size={iconSize.md} />
        </IconButton>
        <Box sx={{ flex: 1 }} />
        <Typography
          component="button"
          variant="body2"
          onClick={() => setStart(weekStart(new Date()))}
          sx={{ border: 0, background: 'none', color: 'primary.main', cursor: 'pointer' }}
        >
          {t('gov.inspections.calendar.today')}
        </Typography>
      </Stack>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'repeat(7, minmax(0, 1fr))' },
          gap: 1,
        }}
      >
        {days.map((day) => {
          const items = (list.data ?? []).filter((r) => onDay(r, day));
          const isToday = onDay({ scheduledFor: today.toISOString() }, day);
          return (
            <HairlineCard
              key={day.toISOString()}
              sx={{
                p: 1.25,
                minHeight: { md: 180 },
                borderColor: isToday ? 'primary.main' : undefined,
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 700,
                  display: 'block',
                  mb: 1,
                  color: isToday ? 'primary.main' : 'text.secondary',
                }}
              >
                {frenchFirstOfMonth(dayLabel.format(day))}
              </Typography>
              <Stack spacing={0.75}>
                {items.length === 0 && (
                  <Typography variant="caption" color="text.secondary">
                    {t('gov.inspections.calendar.none')}
                  </Typography>
                )}
                {items.map((r) => (
                  <Box
                    key={r.id}
                    component="button"
                    onClick={() => navigate(`/inspections/${r.id}`)}
                    sx={{
                      textAlign: 'left',
                      border: 1,
                      borderColor: 'divider',
                      borderRadius: 1,
                      bgcolor: 'background.paper',
                      p: 0.75,
                      cursor: 'pointer',
                      font: 'inherit',
                    }}
                  >
                    <Typography variant="caption" sx={{ fontWeight: 700, display: 'block' }}>
                      {frenchFirstOfMonth(timeLabel.format(new Date(r.scheduledFor)))} ·{' '}
                      {fr ? r.templateNameFr : r.templateNameEn}
                    </Typography>
                    <Typography variant="caption" sx={{ display: 'block' }}>
                      {r.propertyAddress}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      {r.inspectorName ?? t('gov.inspections.unassigned')}
                    </Typography>
                    <Box sx={{ mt: 0.5 }}>
                      <InspectionChip row={r} />
                    </Box>
                  </Box>
                ))}
              </Stack>
            </HairlineCard>
          );
        })}
      </Box>
    </Stack>
  );
}

function UpcomingList({ lg, inspectorId }: { lg: string; inspectorId: string }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const range = useMemo(() => {
    const from = new Date();
    from.setHours(0, 0, 0, 0);
    from.setDate(from.getDate() - 14);
    return { from: from.toISOString(), to: new Date(from.getTime() + 104 * DAY).toISOString() };
  }, []);
  const list = useInspections(lg, range, inspectorId);
  const fr = i18n.language.startsWith('fr');
  const actionsColumn = useRowActionsColumn();

  return (
    <DataTable
      rows={list.data ?? []}
      getRowKey={(r) => r.id}
      isLoading={list.isLoading}
      emptyLabel={t('gov.inspections.empty')}
      pageSize={25}
      initialSortKey="when"
      initialSortDir="asc"
      onRowClick={(r) => navigate(`/inspections/${r.id}`)}
      search={{
        placeholder: t('gov.inspections.search'),
        of: (r) => `${r.reference} ${r.propertyAddress} ${r.inspectorName ?? ''}`,
      }}
      columns={[
        {
          key: 'when',
          label: t('gov.inspections.fields.when'),
          nowrap: true,
          sortValue: (r) => r.scheduledFor,
          cell: (r) => formatDateTime(r.scheduledFor, i18n.language),
        },
        {
          key: 'property',
          label: t('gov.inspections.fields.property'),
          primary: true,
          sortValue: (r) => r.propertyAddress,
          cell: (r) => (
            <Stack>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {r.propertyAddress}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {r.reference} · {fr ? r.templateNameFr : r.templateNameEn}
                {r.permitApplicationReference ? ` · ${r.permitApplicationReference}` : ''}
                {r.fileReference ? ` · ${r.fileReference}` : ''}
              </Typography>
            </Stack>
          ),
        },
        {
          key: 'inspector',
          label: t('gov.inspections.fields.inspector'),
          sortValue: (r) => r.inspectorName ?? '',
          cell: (r) => r.inspectorName ?? t('gov.inspections.unassigned'),
        },
        {
          key: 'status',
          label: t('gov.permits.fields.status'),
          sortValue: (r) => r.status,
          cell: (r) => <InspectionChip row={r} />,
        },
        {
          ...actionsColumn,
          cell: (r) => (
            <RowActions>
              <RowAction
                label={t('gov.inspections.open')}
                icon={Icons.ChevronRight}
                onClick={() => navigate(`/inspections/${r.id}`)}
              />
            </RowActions>
          ),
        },
      ]}
    />
  );
}
